import { shareBySheetKeys } from '@/features/project/review-sheet/utils/search-query-states';
import { logger } from '@/utils';
import { matchProjectDetailPath } from '@/utils/route-match';
import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import type { PinpointMappingType } from '../../models/pinpoint';
import { getQueryParamsByField } from '../../utils/activity-tab-mapping-by-pipoint-field';
import { getScrollTargetPinpointFieldKey } from '../../utils/get-scroll-target-pinpoint-field-key';

type RefElement = {
  element: HTMLDivElement;
  registered: boolean;
};

type PinpointType = PinpointMappingType | null;

type PinpointContextType = {
  pinpoint: PinpointType;
  setPinpoint: Dispatch<SetStateAction<PinpointType>>;
  getPinpointScrollTargetRef: (
    pinpoint: PinpointType | undefined,
  ) => ((element: HTMLDivElement | null) => void) | undefined;
  scrollToPinpoint: (pinpoint: PinpointType) => void;
};

export const PinpointContext = createContext<PinpointContextType | undefined>(
  undefined,
);

const usePinpointContext = (): PinpointContextType => {
  const context = useContext(PinpointContext);
  if (!context) {
    throw Error('usePinpointContext must be used within PinpointProvider.');
  }

  return context;
};

export const usePinpoint = () => {
  const {
    pinpoint,
    setPinpoint,
    getPinpointScrollTargetRef,
    scrollToPinpoint,
  } = usePinpointContext();

  return {
    pinpoint,
    setPinpoint,
    getPinpointScrollTargetRef,
    scrollToPinpoint,
    resetPinpoint() {
      setPinpoint(null);
    },
  };
};

export const PinpointProvider: FC<
  PropsWithChildren<{ initialValue?: PinpointMappingType }>
> = ({ children }) => {
  const [pinpoint, setPinpoint] = useState<PinpointMappingType | null>(null);
  const refsMap = useRef<Record<string, RefElement>>({});
  const isScrolling = useRef(false);

  const setRef = useCallback((element: HTMLDivElement | null, key: string) => {
    if (element) {
      refsMap.current[key] = {
        element,
        registered: true,
      };
    } else if (refsMap.current[key]) {
      // elementがnullの場合(要素がアンマウントされた場合)、
      // registeredフラグをfalseに設定して要素が、もうDOMに存在しないことを示す
      refsMap.current[key].registered = false;
    }
  }, []);

  const getPinpointScrollTargetRef = useCallback(
    (pinpoint: PinpointType | undefined) => {
      const field = pinpoint?.field;
      if (!field) {
        return;
      }
      const targetFieldKey = getScrollTargetPinpointFieldKey({ pinpoint });
      if (!targetFieldKey) {
        return;
      }

      return (element: HTMLDivElement | null) => {
        setRef(element, targetFieldKey);
      };
    },
    [setRef],
  );

  const [searchParams] = useSearchParams();
  const currentPageActiveKey = searchParams.get(
    shareBySheetKeys.Enum.activeKey,
  );
  const isProjectDetailPage = matchProjectDetailPath(location.pathname);

  // 指定されたピンポイントの位置までスクロールする関数
  const scrollToPinpoint = useCallback(
    (pinpoint: PinpointType) => {
      // スクロール処理中の場合は処理をスキップ。連打対策
      if (isScrolling.current) {
        return;
      }

      const activityQueryByField = getQueryParamsByField(pinpoint ?? undefined);
      const pinpointActiveKey = activityQueryByField?.activeKey;

      // プロジェクト詳細ページでない、またはアクティブキーが一致しない場合はスキップ
      if (
        !isProjectDetailPage ||
        (isProjectDetailPage && currentPageActiveKey !== pinpointActiveKey)
      ) {
        return;
      }

      // スクロール処理の開始時刻を記録
      const startTime = Date.now();
      isScrolling.current = true;
      let tryCount = 0;

      // データ読み込みでrefが取得できない場合があるため、再帰的にスクロールを試行する関数
      const tryScroll = () => {
        tryCount++;
        const elapsedTime = Date.now() - startTime;

        // スクロール対象のフィールドキーを取得
        const targetFieldKey = getScrollTargetPinpointFieldKey({
          pinpoint,
          mode: 'startMatchCheck',
          refsMap: refsMap.current,
        });

        if (elapsedTime > 2000) {
          logger.error(
            '箇所指定のスクロール処理がタイムアウトしました。対象が削除されているかデータ取得に時間がかかっています',
            targetFieldKey,
          );
          isScrolling.current = false;
          return;
        }

        if (tryCount > 1) {
          logger.info(
            `スクロール試行: ${tryCount}回目 (経過時間: ${elapsedTime}ms), targetFieldKey: ${targetFieldKey}`,
          );
        }

        // domの生成が間に合わず取得できないことがあるのでループを回す
        if (!targetFieldKey) {
          setTimeout(tryScroll, 200);
          return;
        }

        const targetRef = refsMap.current[targetFieldKey];
        // 要素が登録済みで存在する場合はスクロールを実行
        if (targetRef?.registered && targetRef.element) {
          targetRef.element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          isScrolling.current = false;
          return;
        }

        // 開始から2秒以内の場合は200ms後に再試行。2秒を超える場合はデータ取得側の改善が必要。
        setTimeout(tryScroll, 200);
      };

      tryScroll();
    },
    [currentPageActiveKey, isProjectDetailPage],
  );

  return (
    <PinpointContext.Provider
      value={{
        pinpoint,
        setPinpoint,
        getPinpointScrollTargetRef,
        scrollToPinpoint,
      }}
    >
      {children}
    </PinpointContext.Provider>
  );
};
